import { useToast } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import type { PartRule, SelectionSettings } from '../../../../hooks/useGetSettings';
import { useSuggestSimilarParts } from '../../../../hooks/useSuggestSimilarParts';
import { useUpdateSettings } from '../../../../hooks/useUpdateSettings';
import { SettingsFlow, useSettingsDetailCrumbs } from '../../../../components/settings/primitives';
import PartAllowanceCatalogStep from './PartAllowanceCatalogStep';
import PartAllowanceDetailsStep from './PartAllowanceDetailsStep';
import {
    draftFromRule,
    draftFromSearchResult,
    draftToRule,
    usePositionOptionsByPart,
    withPositionOptions,
    type PartAllowanceDraft,
} from './partAllowanceDrafts';
import { partRuleListBySlug } from './partAllowanceLists';

export interface PartAllowanceFlowSectionProps {
    /** The list being added to, as its URL slug: allowed / forbidden / flagged. */
    listSlug: string;
    data: SelectionSettings;
    oemOptions: Array<{ _id: string; name: string }>;
    refetch?: (options?: RefetchOptions) => Promise<QueryObserverResult<unknown, Error>>;
}

type StepId = 'parts' | 'details';

/**
 * Add parts to one allowance list, full screen and step by step: which parts,
 * then what each of them is allowed to be. Replaces the part catalog modal, so
 * browsing the catalog gets the whole window and the columns that decide the
 * rule are the same click-to-edit cells the table itself uses.
 *
 * `?rule=<index>` reopens one saved rule instead, which skips straight to the
 * second step — there is nothing to choose when the part is already known.
 *
 * Saving writes the whole selection subdocument through POST /settings/update,
 * which is how that endpoint works, so the new rules are merged into the current
 * list here rather than staged on the section's draft.
 */
const PartAllowanceFlowSection = ({
    listSlug,
    data,
    oemOptions,
    refetch,
}: PartAllowanceFlowSectionProps) => {
    const navigate = useNavigate();
    const toast = useToast();
    const updateSettings = useUpdateSettings();
    const suggestMutation = useSuggestSimilarParts();
    const positionOptionsByPart = usePositionOptionsByPart();
    const [searchParams] = useSearchParams();

    const list = partRuleListBySlug(listSlug);
    const rules = useMemo<PartRule[]>(
        () => (list ? data[list.id] ?? [] : []),
        [data, list],
    );

    // An index into the saved list, set when the table's edit action opened this
    // screen. Anything else — a stale link, a removed row — is the add case.
    const editParam = searchParams.get('rule');
    const editIndex = editParam !== null && /^\d+$/.test(editParam) ? Number(editParam) : null;
    const editingRule = editIndex !== null ? rules[editIndex] : undefined;
    const isEditMode = !!editingRule;

    const [stepId, setStepId] = useState<StepId>(isEditMode ? 'details' : 'parts');
    const [drafts, setDrafts] = useState<PartAllowanceDraft[]>(() =>
        editingRule ? [draftFromRule(editingRule, oemOptions)] : [],
    );
    const [suggestingId, setSuggestingId] = useState<number | null>(null);

    // An unknown list slug has nothing to add to, so fall back to the table.
    useEffect(() => {
        if (!list) navigate('/settings/part-allowances', { replace: true });
    }, [list, navigate]);

    // A saved rule stores the position group it was set to but not the groups
    // its part offers, and the catalog those come from is its own query, so the
    // draft's options are filled in once it lands.
    useEffect(() => {
        if (positionOptionsByPart.size === 0) return;
        setDrafts((prev) => {
            const next = prev.map((draft) =>
                withPositionOptions(draft, positionOptionsByPart.get(draft.partTerminologyId)),
            );
            return next.some((draft, index) => draft !== prev[index]) ? next : prev;
        });
    }, [positionOptionsByPart]);

    useSettingsDetailCrumbs(
        list
            ? [
                  {
                      label: isEditMode
                          ? editingRule?.partTerminologyName || 'Part'
                          : `Add to ${list.label}`,
                  },
              ]
            : [],
    );

    const closeFlow = useCallback(() => {
        navigate(list ? `/settings/part-allowances?list=${list.slug}` : '/settings/part-allowances');
    }, [navigate, list]);

    const selectedIds = useMemo(
        () => new Set(drafts.map((draft) => draft.partTerminologyId)),
        [drafts],
    );

    const handleToggle = (draft: PartAllowanceDraft) => {
        setDrafts((prev) => {
            const existing = prev.findIndex(
                (entry) => entry.partTerminologyId === draft.partTerminologyId,
            );
            if (existing >= 0) return prev.filter((_, index) => index !== existing);
            return [...prev, withPositionOptions(draft, positionOptionsByPart.get(draft.partTerminologyId))];
        });
    };

    const handlePatch = (partTerminologyId: number, patch: Partial<PartAllowanceDraft>) => {
        setDrafts((prev) =>
            prev.map((draft) =>
                draft.partTerminologyId === partTerminologyId ? { ...draft, ...patch } : draft,
            ),
        );
    };

    /** One value written to every ticked row, in a single update. */
    const handlePatchMany = (
        partTerminologyIds: Set<number>,
        build: (draft: PartAllowanceDraft) => Partial<PartAllowanceDraft>,
    ) => {
        setDrafts((prev) =>
            prev.map((draft) =>
                partTerminologyIds.has(draft.partTerminologyId)
                    ? { ...draft, ...build(draft) }
                    : draft,
            ),
        );
    };

    const handleRemove = (partTerminologyId: number) => {
        setDrafts((prev) =>
            prev.filter((draft) => draft.partTerminologyId !== partTerminologyId),
        );
    };

    /** Parts like this one, dropped in under it as further rows to configure. */
    const handleSuggest = async (partTerminologyId: number) => {
        const draft = drafts.find((entry) => entry.partTerminologyId === partTerminologyId);
        if (!draft) return;
        setSuggestingId(partTerminologyId);
        try {
            const result = await suggestMutation.mutateAsync({
                partTerminologyName: draft.partTerminologyName,
                categoryId: draft.categoryId,
                subcategoryId: draft.subcategoryId,
            });
            let added = 0;
            setDrafts((prev) => {
                const index = prev.findIndex(
                    (entry) => entry.partTerminologyId === partTerminologyId,
                );
                if (index < 0) return prev;
                const existing = new Set(prev.map((entry) => entry.partTerminologyId));
                const additions = result.suggestions
                    .filter((suggestion) => !existing.has(suggestion.partTerminologyId))
                    .map((suggestion) =>
                        withPositionOptions(
                            draftFromSearchResult(suggestion, true),
                            positionOptionsByPart.get(suggestion.partTerminologyId),
                        ),
                    );
                added = additions.length;
                if (additions.length === 0) return prev;
                const next = [...prev];
                next.splice(index + 1, 0, ...additions);
                return next;
            });
            toast({
                title:
                    added === 0
                        ? 'No new suggestions found'
                        : `${added} suggestion${added === 1 ? '' : 's'} added`,
                status: added === 0 ? 'info' : 'success',
                duration: 3000,
            });
        } catch {
            toast({
                title: 'Could not generate suggestions',
                status: 'error',
                duration: 3000,
            });
        } finally {
            setSuggestingId(null);
        }
    };

    const handleSave = () => {
        if (!list || drafts.length === 0) return;
        const saved = drafts.map((draft) => draftToRule(draft, list.showInsurance));
        const nextRules =
            editIndex !== null
                ? rules.map((rule, index) => (index === editIndex ? saved[0] : rule))
                : [...rules, ...saved];

        updateSettings.mutate(
            { selection: { ...data, [list.id]: nextRules } },
            {
                onSuccess: () => {
                    toast({
                        title: isEditMode ? 'Part updated' : 'Parts added',
                        description: isEditMode
                            ? `${drafts[0].partTerminologyName} has been saved.`
                            : `${saved.length} part${saved.length === 1 ? '' : 's'} added to ${list.title}.`,
                        status: 'success',
                        duration: 3000,
                        isClosable: true,
                    });
                    refetch?.();
                    closeFlow();
                },
                onError: (error) => {
                    toast({
                        title: isEditMode ? 'Could not update part' : 'Could not add parts',
                        description: error.message || 'Please try again.',
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                    });
                },
            },
        );
    };

    if (!list) return null;

    const hasParts = drafts.length > 0;

    return (
        <SettingsFlow
            steps={
                isEditMode
                    ? [{ id: 'details', label: 'Choose allowables' }]
                    : [
                          { id: 'parts', label: 'Choose Parts' },
                          {
                              id: 'details',
                              label: 'Choose allowables',
                              count: drafts.length,
                              isDisabled: !hasParts,
                          },
                      ]
            }
            activeStepId={stepId}
            onStepSelect={(id) => {
                if (id === 'details' && !hasParts) return;
                setStepId(id as StepId);
            }}
            onCancel={closeFlow}
            onBack={!isEditMode && stepId === 'details' ? () => setStepId('parts') : undefined}
            onNext={() => setStepId('details')}
            nextLabel={hasParts ? `Next (${drafts.length})` : 'Next'}
            isNextDisabled={!hasParts}
            onSave={handleSave}
            saveLabel={isEditMode ? 'Save Part' : 'Add Parts'}
            isSaveDisabled={!hasParts}
            isSaving={updateSettings.isPending}
        >
            {stepId === 'parts' ? (
                <PartAllowanceCatalogStep selectedIds={selectedIds} onToggle={handleToggle} />
            ) : (
                <PartAllowanceDetailsStep
                    drafts={drafts}
                    oemOptions={oemOptions}
                    showInsurance={list.showInsurance}
                    onPatch={handlePatch}
                    onRemove={handleRemove}
                    onPatchMany={handlePatchMany}
                    onSuggest={isEditMode ? undefined : handleSuggest}
                    suggestingId={suggestingId}
                />
            )}
        </SettingsFlow>
    );
};

export default PartAllowanceFlowSection;
